<template>
  <div class="md-recommendation">
    <div class="recommendation-header">
      <span class="header-icon-wrap">
        <i class="fa fa-lightbulb-o header-icon"></i>
      </span>
      <div class="header-pill" @click="expanded = !expanded">
        <i class="fa fa-hand-pointer-o pill-icon"></i>
        <span class="pill-text">{{ headerTitle }}</span>
        <i class="fa fa-chevron-down pill-arrow" :class="{ 'is-open': expanded }"></i>
      </div>
    </div>
    <transition name="fade-slide">
      <div v-show="expanded" class="recommendation-cards">
        <div
          v-for="(agent, idx) in parsedAgents"
          :key="idx"
          class="agent-card"
          :class="[`agent-card-color-${agent.color || getColor(idx)}`]"
        >
          <h4 class="agent-card-title">{{ agent.name }}</h4>
          <ul class="skill-list">
            <li
              v-for="(skill, sIdx) in agent.skills"
              :key="sIdx"
              class="skill-item"
              @click="handleSkillClick(skill)"
            >
              <i class="fa fa-check-circle skill-icon" :class="[`skill-icon-${agent.color || getColor(idx)}`]"></i>
              <span>{{ skill.label }}</span>
            </li>
          </ul>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const COLOR_POOL = ['blue', 'green', 'purple', 'orange']

export default {
  name: 'MdRecommendation',
  props: {
    title: { type: String, default: '' },
    agents: { type: String, default: '' },
    dataList: { type: String, default: '' },
    action: { type: String, default: '' },
    _rawJson: { type: String, default: '' },
    _componentKey: { type: String, default: '' }
  },
  data() {
    return {
      expanded: true
    }
  },
  computed: {
    headerTitle() {
      return this.title || this.$t('workbench.recommendTitle') || '猜你关注'
    },
    parsedAgents() {
      const raw = this._parseJson(this._rawJson)
      const rawAgents = this._parseJson(this.agents) ||
        (raw && raw.agents)
      if (Array.isArray(rawAgents) && rawAgents.length) return rawAgents

      // 兼容 recommended-question 格式：{ title, items: [string] }
      const rawList = this._parseJson(this.dataList) ||
        (raw && raw.dataList)
      if (Array.isArray(rawList) && rawList.length) {
        return rawList.map(group => ({
          name: group.title || '',
          skills: Array.isArray(group.items)
            ? group.items.map(item =>
              typeof item === 'string' ? { label: item } : item
            )
            : []
        }))
      }

      return []
    }
  },
  methods: {
    _parseJson(value) {
      if (!value) return null
      if (typeof value === 'object') return value
      if (typeof value !== 'string') return null
      const cleaned = value.replace(/\n/g, '')
      try {
        return JSON.parse(cleaned)
      } catch {
        try {
          const normalized = cleaned.replace(/'/g, '"').replace(/,\s*([}\]])/g, '$1')
          return JSON.parse(normalized)
        } catch {
          return null
        }
      }
    },
    getColor(idx) {
      return COLOR_POOL[idx % COLOR_POOL.length]
    },
    handleSkillClick(skill) {
      const value = skill.value || skill.label
      if (!value) return
      const message = `[card-action]${value}`
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action: this.action, value, message }
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.md-recommendation {
  margin-top: 16px;
}

/* Header */
.recommendation-header {
  display: flex;
  align-items: center;
}

.header-icon-wrap {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f0fdf4;
  margin-right: 12px;
  flex-shrink: 0;
}

.header-icon {
  color: #16a34a;
  font-size: 14px;
}

.header-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(to right, #f0fdf4, #eff6ff);
  border: 1px solid #bbf7d0;
  border-radius: 9999px;
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.pill-icon {
  color: #22c55e;
  font-size: 13px;
}

.pill-text {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.pill-arrow {
  color: #94a3b8;
  font-size: 11px;
  transition: transform 0.25s;

  &.is-open {
    transform: rotate(180deg);
  }
}

/* Cards container */
.recommendation-cards {
  display: flex;
  gap: 12px;
  margin-top: 12px;
  padding-left: 44px;
}

/* Single card */
.agent-card {
  flex: 0 0 auto;
  width: auto;
  min-width: 140px;
  max-width: 260px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e0e7ff;
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

/* Border color variants */
.agent-card-color-blue   { border-color: #bfdbfe; }
.agent-card-color-green { border-color: #bbf7d0; }
.agent-card-color-purple { border-color: #d8b4fe; }
.agent-card-color-orange { border-color: #fed7aa; }

h4.agent-card-title,
.agent-card-title {
  font-size: 13px !important;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 10px 0;
  line-height: 1.4;
}

.skill-list {
  list-style: none;
  padding-left: 0;
  padding-right: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: fit-content;
}

.skill-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  padding: 0;
  margin: 0;
  transition: color 0.15s;
  line-height: 1.4;
  text-align: left;
  list-style: none;

  &:hover {
    color: #1d4ed8;
  }
}

.skill-icon {
  flex-shrink: 0;
  font-size: 12px;
  vertical-align: middle;
}

.skill-icon-blue   { color: #3b82f6; }
.skill-icon-green  { color: #22c55e; }
.skill-icon-purple { color: #a855f7; }
.skill-icon-orange { color: #f97316; }

/* Transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.25s, transform 0.25s;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
